// =============================================================================
// features/sales/campaigns/operate/OperatePage.tsx — Rota
// `/campanhas/:campaignId/operar` (F35-S10, doc 26 §6.8).
//
// Gate de permissão + flag (mesmo padrão de CampaignsPage.tsx/AgendaPage.tsx):
// `sales_campaigns:operate` E `sales.campaigns.enabled`. `OperateContent`
// (que dispara claim-next) só monta depois que os dois gates passam — nunca
// reserva um cliente para um ator sem a permissão certa.
// =============================================================================

import * as React from 'react';
import { useParams } from 'react-router-dom';

import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useAuth } from '../../../../lib/auth-store';

import { OperateContent } from './OperateContent';

const FLAG_KEY = 'sales.campaigns.enabled';
const OPERATE_PERMISSION = 'sales_campaigns:operate';

export function OperatePage(): React.JSX.Element {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { hasPermission } = useAuth();
  const { enabled: flagEnabled, isLoading: flagLoading } = useFeatureFlag(FLAG_KEY);

  const canOperate = hasPermission(OPERATE_PERMISSION);

  if (!campaignId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <p className="font-sans text-sm text-ink-3 max-w-xs">Campanha não informada na URL.</p>
      </div>
    );
  }

  if (!canOperate) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <p className="font-sans text-sm text-ink-3 max-w-xs">
          Você não tem acesso para operar campanhas comerciais. Fale com um administrador se
          precisar de acesso.
        </p>
      </div>
    );
  }

  if (flagLoading) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="h-16 rounded-md animate-pulse"
            style={{ background: 'var(--surface-muted)' }}
            aria-hidden="true"
          />
        ))}
      </div>
    );
  }

  if (!flagEnabled) {
    return (
      <div
        className="flex items-start gap-3 px-4 py-3 rounded-sm border"
        style={{
          background: 'var(--warning-bg)',
          borderColor: 'var(--warning)',
          borderLeftWidth: 3,
        }}
        role="alert"
      >
        <div>
          <p className="font-sans text-sm font-semibold text-ink">Módulo desabilitado</p>
          <p className="font-sans text-xs text-ink-3 mt-0.5">
            A flag <code className="font-mono">{FLAG_KEY}</code> está desativada para esta
            organização.
          </p>
        </div>
      </div>
    );
  }

  return <OperateContent campaignId={campaignId} />;
}
